import { comboSeriesColor, orderedComboSeries } from "../../logic/chartCombo";
import type { ExcelChartLayout, ExcelComboSeriesSettings } from "../../types";

// Live shape preview of a combo chart for the workbook preview panel. It uses sample values
// (no query has run in the editor), so it shows what the per-series settings do - which rows
// are columns vs lines, which axis each uses, colours, legend names - not the real numbers.
// Mirrors the renderer's layout: columns on each axis are clustered per category, lines are
// drawn above them, and the right axis only appears when a primary and a secondary series
// both exist.

const WIDTH = 360;
const HEIGHT = 170;
const PAD = { top: 12, right: 34, bottom: 24, left: 34 };

// Deterministic sample shape per series so the preview doesn't jump while the user types.
function sampleValues(position: number, count: number, isRatio: boolean) {
    return Array.from({ length: count }, (_, index) => {
        const wave = Math.sin((index + 1) * 1.3 + position * 0.9);
        return isRatio ? 0.55 + 0.3 * ((wave + 1) / 2) : 30 + 50 * ((wave + 1) / 2) - position * 6;
    });
}

interface ComboChartPreviewProps {
    chart: ExcelChartLayout;
    categories: string[];
    // Labels of derived rows with a percent-like result, sampled on a 0-1 scale.
    ratioRowLabels?: string[];
}

export function ComboChartPreview({ chart, categories, ratioRowLabels = [] }: ComboChartPreviewProps) {
    const listed = orderedComboSeries(chart.combo).filter((item) => item.seriesKey.trim());
    const cats = categories.length ? categories : ["Value"];
    const ratioKeys = new Set(ratioRowLabels.map((label) => label.trim().toLowerCase()));
    const hasPrimary = listed.some((item) => item.axis !== "secondary");
    const useSecondary = hasPrimary && listed.some((item) => item.axis === "secondary");

    if (!listed.length) {
        return <div className="rc-combo-preview-empty">Add series to see the combo chart preview.</div>;
    }

    type Plotted = { item: ExcelComboSeriesSettings; position: number; values: number[]; secondary: boolean; color: string; name: string };
    const plotted: Plotted[] = listed.map((item, position) => ({
        item,
        position,
        values: sampleValues(position, cats.length, ratioKeys.has(item.seriesKey.trim().toLowerCase())),
        secondary: useSecondary && item.axis === "secondary",
        color: comboSeriesColor(item, position, chart.settings?.palette),
        name: (item.label || "").trim() || item.seriesKey.trim(),
    }));

    const maxOf = (secondary: boolean) => Math.max(1e-9, ...plotted.filter((p) => p.secondary === secondary).flatMap((p) => p.values)) * 1.1;
    const primaryMax = maxOf(false);
    const secondaryMax = useSecondary ? maxOf(true) : 1;

    const plotW = WIDTH - PAD.left - (useSecondary ? PAD.right : 12);
    const plotH = HEIGHT - PAD.top - PAD.bottom;
    const band = plotW / cats.length;
    const y = (value: number, secondary: boolean) => PAD.top + plotH - (value / (secondary ? secondaryMax : primaryMax)) * plotH;
    const xCenter = (index: number) => PAD.left + band * index + band / 2;

    const columns = plotted.filter((p) => p.item.chartType !== "line");
    const lines = plotted.filter((p) => p.item.chartType === "line");
    const groupWidth = band * 0.7;
    const barWidth = columns.length ? Math.max(2, groupWidth / columns.length - 2) : 0;

    const summary = plotted
        .map((p) => `${p.name}: ${p.item.chartType === "line" ? "line" : "column"}${p.secondary ? " on the right axis" : ""}`)
        .join("; ");

    return (
        <figure className="rc-combo-preview">
            <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={`Combo chart preview with sample values. ${summary}.`}>
                {[0, 0.5, 1].map((fraction) => (
                    <line key={fraction} className="rc-combo-preview-grid" x1={PAD.left} x2={PAD.left + plotW} y1={PAD.top + plotH * (1 - fraction)} y2={PAD.top + plotH * (1 - fraction)} />
                ))}
                <text className="rc-combo-preview-axis" x={PAD.left - 6} y={PAD.top + 4} textAnchor="end">max</text>
                <text className="rc-combo-preview-axis" x={PAD.left - 6} y={PAD.top + plotH} textAnchor="end">0</text>
                {useSecondary && (
                    <>
                        <text className="rc-combo-preview-axis" x={PAD.left + plotW + 6} y={PAD.top + 4}>max</text>
                        <text className="rc-combo-preview-axis" x={PAD.left + plotW + 6} y={PAD.top + plotH}>0</text>
                    </>
                )}

                {cats.map((category, index) => (
                    <g key={`${category}_${index}`}>
                        {columns.map((p, columnIndex) => {
                            const top = y(p.values[index], p.secondary);
                            const x = xCenter(index) - groupWidth / 2 + columnIndex * (barWidth + 2) + 1;
                            return <rect key={p.position} x={x} y={top} width={barWidth} height={Math.max(0, PAD.top + plotH - top)} rx={Math.min(3, barWidth / 2)} fill={`#${p.color}`} />;
                        })}
                        <text className="rc-combo-preview-axis" x={xCenter(index)} y={HEIGHT - 8} textAnchor="middle">{category.length > 6 ? `${category.slice(0, 5)}…` : category}</text>
                    </g>
                ))}

                {lines.map((p) => (
                    <g key={p.position}>
                        <polyline
                            className="rc-combo-preview-line"
                            points={p.values.map((value, index) => `${xCenter(index)},${y(value, p.secondary)}`).join(" ")}
                            stroke={`#${p.color}`}
                        />
                        {p.values.map((value, index) => (
                            <circle key={index} className="rc-combo-preview-dot" cx={xCenter(index)} cy={y(value, p.secondary)} r={4} fill={`#${p.color}`} />
                        ))}
                    </g>
                ))}
            </svg>
            <figcaption className="rc-combo-preview-legend">
                {plotted.map((p) => (
                    <span key={p.position}>
                        <i className={p.item.chartType === "line" ? "line" : "column"} style={{ background: `#${p.color}` }} aria-hidden="true" />
                        {p.name}
                        {p.secondary && <small> (right axis)</small>}
                    </span>
                ))}
                <small className="rc-combo-preview-note">Sample values - the workbook uses the report data.</small>
            </figcaption>
        </figure>
    );
}
